/**
 * PowerOfAttorney flow DTO + completeness predicate (PF v2 native module).
 *
 * The module's durable state IS this DTO, stored at
 * `productConfigs["${cartItemId}|power_of_attorney"]` (MODULE-CONTRACT Pattern A).
 * The DTO carries the uploaded GCS filenames — PoA files attach to
 * `/order/{sessionId}/…` at upload time, so the DTO is draft-only (resume display
 * + completeness gate), never submitted at v2 order placement.
 *
 * Field names + optionality are IDENTICAL to the legacy adapter DTO
 * (`adapters/powerOfAttorney/powerOfAttorneyDTO.ts`) so a draft saved under the old
 * adapter round-trips byte-identical (§6 DONE gate). Do NOT rename
 * `representativePowerOfAttorneyFilename` — the legacy name is load-bearing for
 * wire-compat. `PF_VERSION` stays frozen at 3.
 *
 * ZERO React / store imports — `modules/completeness.ts` (which `store/selectors.ts`
 * imports) pulls `powerOfAttorneyIsComplete` from here, so this file must stay pure.
 */

/** Persisted/wire shape. */
export interface PowerOfAttorneyConfigDTO {
  /** Main POA GCS filename. Legacy: optional, absent when no file. */
  powerOfAttorneyFilename?: string
  /** Representative POA GCS filename. LEGACY NAME — keep it verbatim. */
  representativePowerOfAttorneyFilename?: string
  /** Supplementary docs (0..15). */
  otherFilenames?: string[]
  /**
   * ADDITIVE (new, optional). Whether reps are required, captured at config time
   * so the pure predicate can gate without reading live `targetUser`. Absent on
   * legacy drafts → treated as false (matches legacy's non-reps branch). Written
   * ONLY on user interaction (never on mount) so the byte-identical round-trip
   * holds — see `usePoaConfigController`.
   */
  requiresRepresentative?: boolean
}

/**
 * Read-time fallback for the ops hook's field-merge. NEVER written on mount
 * (read-only-mount invariant, MODULE-CONTRACT §3).
 */
export const POWER_OF_ATTORNEY_DEFAULT: PowerOfAttorneyConfigDTO = {
  powerOfAttorneyFilename: undefined,
  representativePowerOfAttorneyFilename: undefined,
  otherFilenames: [],
  requiresRepresentative: false,
}

/**
 * Pure completeness predicate — a faithful port of the legacy
 * `isPowerOfAttorneyStepValid` (`PowerOfAttorneyStep.context.ts:103-117`). Reads
 * the persisted DTO filenames + the captured `requiresRepresentative`.
 *
 * The legacy predicate is LOOSE and surprising — reproduce it exactly to avoid a
 * gating regression:
 *   - non-reps orgs: always complete (Save always enabled, empty Save confirms).
 *   - reps orgs with ZERO POA docs: "complete" (legacy quirk — empty reps-order).
 *   - reps orgs with at least one POA doc: requires BOTH main + reps.
 * Undefined DTO (untouched flow) → requiresRep=false → true (fresh-mount parity).
 */
export const powerOfAttorneyIsComplete = (dto: PowerOfAttorneyConfigDTO | undefined): boolean => {
  const requiresRep = !!dto?.requiresRepresentative
  const main = !!dto?.powerOfAttorneyFilename
  const reps = !!dto?.representativePowerOfAttorneyFilename
  // Legacy: only gates when reps are required AND at least one POA doc exists.
  if (requiresRep && (main || reps)) return main && reps
  return true
}
